import React, { useEffect, useState } from 'react';
import axios from 'axios';
import api from '../api/api';
import './AiAssistant.css';

const SYSTEM_CHANGE_CONFIRMATION = 'CONFIRM SYSTEM CHANGE';

interface Settings {
  enabled: boolean;
  model: string;
  dailyRequestLimit: number;
  apiKeyConfigured: boolean;
}

const errorMessage = (error: unknown) => axios.isAxiosError<{ message?: string }>(error)
  ? error.response?.data?.message || 'Не вдалося зберегти налаштування Gemini.'
  : 'Не вдалося зберегти налаштування Gemini.';

const AiAssistantSettings: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [settings, setSettings] = useState<Settings | null>(null);
  const [apiKey, setApiKey] = useState('');
  const [clearApiKey, setClearApiKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api.get<Settings>('/ai-assistant/settings', { signal: controller.signal })
      .then(({ data }) => { if (!controller.signal.aborted) setSettings(data); })
      .catch(() => {
        if (!controller.signal.aborted) setError('Не вдалося завантажити налаштування Gemini. Оновіть сторінку.');
      });
    return () => controller.abort();
  }, []);

  const save = async () => {
    if (!settings || !canEdit || saving) return;
    if (!window.confirm('Підтвердити зміну системних налаштувань Gemini? Зміна діє для всієї CRM.')) return;
    setSaving(true); setError(''); setMessage('');
    try {
      const { data } = await api.put<Settings>('/ai-assistant/settings', {
        enabled: settings.enabled, model: settings.model, dailyRequestLimit: settings.dailyRequestLimit,
        apiKey: apiKey.trim() || null, clearApiKey,
        confirmation: SYSTEM_CHANGE_CONFIRMATION,
      });
      setSettings(data); setApiKey(''); setClearApiKey(false);
      setMessage('Налаштування Gemini збережено. Перевірте відповідь у забезпеченні замовлень.');
    } catch (error: unknown) { setError(errorMessage(error)); }
    finally { setSaving(false); }
  };

  return <section className="ai-assistant" aria-label="Налаштування ШІ">
    <h3>ШІ-помічник · Google Gemini</h3>
    <p>Пілотна версія: поради щодо закупівель за готовими темами. У Google надсилаються лише загальні запитання,
      без даних CRM. Точні кількості та дефіцит розраховуються й відображаються локально.</p>
    <p>Ключ створюється в <a href="https://aistudio.google.com/api-keys" target="_blank" rel="noreferrer">Google AI Studio</a>.
      Використання залежить від тарифу й квот Google; підписка Gemini не замінює API-доступ.
      Не надсилайте ключ у чат. Він зберігається зашифрованим лише на сервері.</p>
    <p>Статус «налаштований» означає лише наявність ключа, а не перевірене підключення.
      Модель має бути доступною вашому проєкту Google; для Gemini 3 використовується короткий режим міркування.</p>
    {error && <p role="alert">{error}</p>}
    {message && <p role="status">{message}</p>}
    {!settings && !error && <p role="status">Завантажуємо налаштування…</p>}
    {settings && <fieldset disabled={!canEdit || saving}>
      <legend>Доступ та обмеження</legend>
      <label><input type="checkbox" checked={settings.enabled}
        onChange={event => setSettings({ ...settings, enabled: event.target.checked })} />Увімкнути ШІ-помічника</label>
      <label>Модель Gemini<input value={settings.model} maxLength={67}
        onChange={event => setSettings({ ...settings, model: event.target.value })} /></label>
      <label>Ліміт запитів на день для всієї CRM<input type="number" min={1} max={100}
        value={settings.dailyRequestLimit}
        onChange={event => setSettings({ ...settings, dailyRequestLimit: Number(event.target.value) })} /></label>
      <label>API-ключ Gemini<input type="password" autoComplete="new-password" value={apiKey} maxLength={4096}
        disabled={clearApiKey} placeholder={settings.apiKeyConfigured ? 'Ключ збережено; порожнє поле не змінює його' : 'Вставте API-ключ'}
        onChange={event => setApiKey(event.target.value)} /></label>
      <p>Вставте лише повне значення ключа через кнопку копіювання Google AI Studio,
        не його назву, JSON-файл чи команду налаштування.</p>
      <label><input type="checkbox" checked={clearApiKey} onChange={event => {
        setClearApiKey(event.target.checked);
        if (event.target.checked) { setApiKey(''); setSettings({ ...settings, enabled: false }); }
      }} />Видалити збережений ключ і вимкнути помічника</label>
      <p>Мінімальний інтервал — 30 секунд; невдалі й скасовані запити також витрачають ліміт.
        Ліміт зберігається після перезапуску та оновлюється опівночі за Києвом.
        Це обмеження кількості запитів, не грошовий бюджет — налаштуйте квоти також у Google.</p>
      <button type="button" onClick={save}>{saving ? 'Зберігаємо…' : 'Зберегти Gemini'}</button>
    </fieldset>}
  </section>;
};

export default AiAssistantSettings;
